import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previews } from '../data/previews';
export function Previews() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const syncEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 8);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 8);
  }, []);
  useEffect(() => {
    syncEdges();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', syncEdges, {
      passive: true
    });
    window.addEventListener('resize', syncEdges);
    return () => {
      el.removeEventListener('scroll', syncEdges);
      window.removeEventListener('resize', syncEdges);
    };
  }, [syncEdges]);
  const scrollByCard = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 20 : el.clientWidth * 0.8;
    el.scrollBy({
      left: step * dir,
      behavior: 'smooth'
    });
  };
  return <section aria-label="Pages from inside the ebook" className="bg-transparent" data-id="element-133" data-mp="2zs9j708fyrlz">
      <div className="mx-auto max-w-6xl px-5 pb-12 pt-2" data-id="element-134" data-mp="2uu7ykavdak8e">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-135" data-mp="2utpwgxm0xbsk">
          <div data-id="element-136" data-mp="2pvod0bkoragk">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-grape" data-id="element-137" data-mp="2pwg0ez44q1vr">
              👀 A look inside
            </p>
            <h2 className="mt-2 font-display text-2xl font-black text-plum sm:text-3xl" data-id="element-138" data-mp="2h2kqlds2bn9s">
              Real pages from the method
            </h2>
            <p className="mt-2 max-w-xl text-sm text-plum/60" data-id="element-139" data-mp="2c4jc765l0c4n">
              This is how every lesson looks: one idea per page, illustrated, with Hangul,
              romanization and the meaning side by side.
            </p>
          </div>

          <div className="flex gap-2" data-id="element-140" data-mp="227mxujt04vs6">
            <button type="button" onClick={() => scrollByCard(-1)} disabled={atStart} aria-label="Previous pages" className="flex h-11 w-11 items-center justify-center rounded-full border border-grape/25 bg-white text-grape transition-[background-color,opacity] duration-150 ease-out hover:bg-lilac-soft disabled:opacity-35 focus:outline-none focus-visible:ring-2 focus-visible:ring-grape" data-id="element-141" data-mp="1shey69fbalep">
              <ChevronLeftIcon className="h-5 w-5" data-id="element-142" data-mp="2g6zjv02unwro" />
            </button>
            <button type="button" onClick={() => scrollByCard(1)} disabled={atEnd} aria-label="Next pages" className="flex h-11 w-11 items-center justify-center rounded-full border border-grape/25 bg-white text-grape transition-[background-color,opacity] duration-150 ease-out hover:bg-lilac-soft disabled:opacity-35 focus:outline-none focus-visible:ring-2 focus-visible:ring-grape" data-id="element-143" data-mp="2g9dbeyybmquh">
              <ChevronRightIcon className="h-5 w-5" data-id="element-144" data-mp="nhgquxkz84lj" />
            </button>
          </div>
        </div>

        <ul ref={trackRef} className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-145" data-mp="iix44uyk6os4">
          {previews.map((p, i) => <li key={p.id} className="w-[240px] shrink-0 snap-start sm:w-[280px]" data-id="element-146" data-mp="sbuf8g181bgp" data-mpi={i}>
              <figure className="flex h-full flex-col overflow-hidden rounded-2xl border border-grape/15 bg-cream shadow-[0_16px_36px_-28px_rgba(67,24,15,0.55)]" data-id="element-147" data-mp="3vqgqb2gvtzu9">
                <img src={p.img} alt={p.title} width={1080} height={1350} loading={i < 2 ? 'eager' : 'lazy'} decoding="async" sizes="(min-width: 640px) 280px, 240px" className="aspect-[4/5] w-full bg-lilac-soft object-cover object-top" data-id="element-148" data-mp="eg0eikalpseo" />
                <figcaption className="flex flex-1 flex-col gap-1 border-t border-grape/10 p-4" data-id="element-149" data-mp="3qoiui49rte66">
                  <h3 className="text-sm font-bold text-plum" data-id="element-150" data-mp="3vnn1co982ztc">{p.title}</h3>
                  <p className="text-xs leading-relaxed text-plum/60" data-id="element-151" data-mp="3gyjxweq0wsz">{p.caption}</p>
                </figcaption>
              </figure>
            </li>)}
        </ul>

        <p className="mt-1 text-center text-xs font-medium text-plum/45 sm:hidden" data-id="element-152" data-mp="dgpgs4qr7nw8">
          Swipe to see more pages →
        </p>
      </div>
    </section>;
}